// https://github.com/calm/evexia-android/blob/e43d2ab29a4883cb20e751583754c38eed12b449/repository/src/main/java/com/calm/repository/fusion/ProgramRepository.kt#L27
import {
    getProgramRecommendations,
    sortPackItemsByRecommendation,
} from '@rhg/utils/pack-program';
import { useActiveCircleId } from '../use-active-circle';
import { usePacksForWorkflows } from './useFetchPacks';
import { useProgramAndWorkflowIds } from './useProgramAndWorkflowIds';

export interface UseProgramRecommendationsOptions {
    circleId: number;
    showAll?: boolean;
    isSorted?: boolean;
}

/** Extracts all programs and guides from the program recommendations of the given circle */
export function useProgramRecommendations({
    circleId,
    showAll,
    isSorted = true,
}: UseProgramRecommendationsOptions) {
    const {
        isFetched: hasFetchedCircleRecommendations,
        shownProgramRecommendations,
        isPending: isPendingCircleRecommendations,
    } = useProgramAndWorkflowIds({
        circleId,
        showAll,
    });

    const {
        packs,
        isFetched: hasFetchedPacks,
        isPending: isPendingPacks,
        contentPackWorkflowsNotComingSoon,
    } = usePacksForWorkflows({
        showAll,
        circleId,
    });

    let programRecommendations = undefined;
    if (packs && packs.length && hasFetchedPacks) {
        const formattedPackItems = getProgramRecommendations({
            packs,
            shownProgramRecommendations,
            contentPackWorkflowsNotComingSoon,
        });
        programRecommendations = isSorted
            ? sortPackItemsByRecommendation(formattedPackItems)
            : formattedPackItems;
    }

    const isEverythingFetched =
        hasFetchedCircleRecommendations && hasFetchedPacks;

    const isPending = isPendingCircleRecommendations || isPendingPacks;
    return {
        programRecommendations,
        isFetched: isEverythingFetched,
        isPending,
    };
}

/** Extracts all programs and guides from the program recommendations of the active circle */
export function useActiveCircleProgramRecommendations(
    props?: Omit<UseProgramRecommendationsOptions, 'circleId'>
) {
    const { activeCircleId } = useActiveCircleId();
    return useProgramRecommendations({ circleId: activeCircleId, ...props });
}
